// File: src/utils/core/dystestLogger.ts

/**
 * DysTest Clinical Logger
 * Enhanced for DysTest Healthcare Platform with clinical error integration
 * Features: PII protection, performance monitoring, clinical error handling,
 *           DystestError integration, HIPAA compliance
 * Author: Uttam Panda
 * Architecture: 5-Domain Core Layer
 */

// Import ALL types that are type-only declarations
import type {
  LogCategory,
  LogDestination,
  LogEntry,
  LoggerConfig,
  ClinicalErrorDetails,
  CanonicalLandmark,
} from "@/types";

// Import enums and runtime values separately
import { LogLevel } from "@/types";

// Import error system
import { DystestError, isClinicalError } from "./errors";

const isJest = typeof process !== "undefined" && process.env['JEST_WORKER_ID'] != null;
const debugLogsEnabled =
  typeof process !== "undefined" &&
  (process.env['VITE_DEBUG_LOGS'] === "true" || process.env['DEBUG_LOGS'] === "true");
// ============================================================================
// INTERNAL INTERFACES (now with mutable properties)
// ============================================================================

interface BatchLogEntry {
  entries: readonly LogEntry[];
  timestamp: string;
  batchId: string;
}

interface CircuitBreakerState {
  failures: number;
  lastFailure: number;
  state: "CLOSED" | "OPEN" | "HALF_OPEN";
}

interface RetryState {
  attempt: number;
  nextRetryTime: number;
  logs: readonly LogEntry[];
}

interface ExternalService {
  name: string;
  log: (entry: LogEntry) => void;
  error: (entry: LogEntry) => void;
}

interface SanitizationRule {
  pattern: RegExp;
  replacement: string;
}

// ============================================================================
// DYSTEST CLINICAL LOGGER
// ============================================================================

export class DystestLogger {
  private static instance: DystestLogger;
  private config: LoggerConfig;
  private logBuffer: LogEntry[] = [];
  private batchTimer: number | null = null;
  private circuitBreakers: Map<LogDestination, CircuitBreakerState> = new Map();
  private retryQueues: Map<LogDestination, RetryState[]> = new Map();
  private performanceMetrics: Map<string, number> = new Map();
  private performanceMarks: Map<string, number> = new Map();
  private externalServices: ExternalService[] = [];
  private sessionId: string;
  private userId?: string | undefined;
  private retryTimer: number | null = null;

  // Default PII fields to sanitize (healthcare specific)
  private readonly DEFAULT_PII_FIELDS = [
    "email",
    "phone",
    "ssn",
    "address",
    "name",
    "ip",
    "creditCard",
    "password",
    "token",
    "session",
    "patientId",
    "mrn",
    "medicalRecord",
    "diagnosis",
    "prescription",
    "dob",
    "dateOfBirth",
    // Clinical specific
    "canonicalLandmark",
    "faceMesh",
    "gazeData",
    "typingPattern",
    "riskScore",
  ];

  // Regex patterns for PII protection
  private readonly PII_PATTERNS: SanitizationRule[] = [
    { pattern: /\b\d{3}-\d{2}-\d{4}\b/g, replacement: "***-**-****" }, // SSN
    { pattern: /\b\d{3}-\d{3}-\d{4}\b/g, replacement: "***-***-****" }, // Phone
    {
      pattern: /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,
      replacement: "***@***.***",
    }, // Email
    { pattern: /\b\d{16}\b/g, replacement: "****************" }, // Credit card
    {
      pattern: /\b(?:patient|user)Id[:\s]+[\w-]+/gi,
      replacement: "patientId: [REDACTED]",
    }, // Patient IDs
    {
      pattern: /\b(?:mrn|medical record)[:\s]+[\w-]+/gi,
      replacement: "MRN: [REDACTED]",
    }, // Medical records
    // Clinical data patterns
    {
      pattern: /\bcanonicalLandmark[:\s]+\[.*?\]/gi,
      replacement: "canonicalLandmark: [REDACTED_CLINICAL_DATA]",
    },
    {
      pattern: /\bgazePoint[:\s]+\[.*?\]/gi,
      replacement: "gazePoint: [REDACTED_CLINICAL_DATA]",
    },
    {
      pattern: /\btypingMetrics[:\s]+\[.*?\]/gi,
      replacement: "typingMetrics: [REDACTED_CLINICAL_DATA]",
    },
  ];

  // Browser capabilities detection
  private hasMemoryAPI: boolean = false;
  private hasPerformanceAPI: boolean = false;

  private constructor(config?: Partial<LoggerConfig>) {
    // Detect browser capabilities
    this.detectBrowserCapabilities();

    this.config = {
      minLevel: config?.minLevel || LogLevel.INFO,
      destinations: config?.destinations || (isJest || !debugLogsEnabled ? [] : ["console"]),
      enableBatching: config?.enableBatching ?? true,
      batchSize: config?.batchSize || 10,
      batchInterval: config?.batchInterval || 5000,
      enablePiiProtection: config?.enablePiiProtection ?? true,
      enablePerformanceTracking: config?.enablePerformanceTracking ?? true,
      maxRetries: config?.maxRetries || 3,
      retryBackoffMs: config?.retryBackoffMs || 1000,
      circuitBreakerThreshold: config?.circuitBreakerThreshold || 5,
      sanitizeFields: config?.sanitizeFields || this.DEFAULT_PII_FIELDS,
      useIdleCallback: config?.useIdleCallback ?? true,
      useSendBeacon: config?.useSendBeacon ?? true,
      environment: config?.environment || "development",
      maxBatchSize: config?.maxBatchSize || 100,
      appVersion: config?.appVersion || "1.0.0",
      remoteEndpoint: config?.remoteEndpoint,
    };

    // Initialize session
    this.sessionId = this.generateId("session");

    // Initialize circuit breakers (using mutable objects)
    this.config.destinations.forEach((dest: LogDestination) => {
      this.circuitBreakers.set(dest, {
        failures: 0,
        lastFailure: 0,
        state: "CLOSED",
      });
      this.retryQueues.set(dest, []);
    });

    // Start batch processing if enabled
    if (this.config.enableBatching && this.config.destinations.length > 0) {
      this.startBatchProcessing();
      this.startRetryTimer();
    }

    // Log initialization
    if (this.config.destinations.length > 0) {
      this.internalLog(LogLevel.INFO, "SYSTEM", "Logger initialized", {
        config: this.sanitizeConfig(this.config),
        sessionId: this.sessionId,
        hasMemoryAPI: this.hasMemoryAPI,
        hasPerformanceAPI: this.hasPerformanceAPI,
      });
    }
  }

  /**
   * Get singleton instance
   */
  static getInstance(config?: Partial<LoggerConfig>): DystestLogger {
    if (!DystestLogger.instance) {
      DystestLogger.instance = new DystestLogger(config);
    }
    return DystestLogger.instance;
  }

  /**
   * Create a namespaced logger for a specific domain
   */
  static createDomainLogger(
    source: string,
    domain: "VISION" | "GAZE" | "KEYSTROKE" | "CLINICAL" | "UI",
    config?: Partial<LoggerConfig>
  ): DystestLogger {
    const logger = DystestLogger.getInstance(config);
    return new Proxy(logger, {
      get(target, prop) {
        if (
          [
            "debug",
            "info",
            "warn",
            "error",
            "critical",
            "logError",
            "logClinicalError",
            "logPerformance",
            "startPerformanceTrace",
            "endPerformanceTrace",
          ].includes(prop as string)
        ) {
          return (...args: unknown[]) => {
            const method = prop as keyof DystestLogger;
            return (target[method] as (...a: unknown[]) => unknown).call(
              target,
              ...args,
              source,
              domain
            );
          };
        }
        return (target as unknown as Record<string | symbol, unknown>)[prop];
      },
    });
  }

  // ============================================================================
  // PUBLIC LOGGING API WITH CLINICAL INTEGRATION
  // ============================================================================

  /**
   * Debug log
   */
  debug(
    message: string,
    data?: Record<string, any>,
    source?: string,
    domain?: string
  ): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      const category: LogCategory = domain
        ? (`${domain}_DEBUG` as LogCategory)
        : "TECHNICAL";
      this.log(LogLevel.DEBUG, category, message, data, source);
    }
  }

  /**
   * Info log
   */
  info(
    message: string,
    data?: Record<string, any>,
    source?: string,
    domain?: string
  ): void {
    if (this.shouldLog(LogLevel.INFO)) {
      const category: LogCategory = domain
        ? (domain as LogCategory)
        : "BUSINESS";
      this.log(LogLevel.INFO, category, message, data, source);
    }
  }

  /**
   * Warn log
   */
  warn(
    message: string,
    data?: Record<string, any>,
    source?: string,
    domain?: string
  ): void {
    if (this.shouldLog(LogLevel.WARN)) {
      const category: LogCategory = domain
        ? (`${domain}_WARN` as LogCategory)
        : "TECHNICAL";
      this.log(LogLevel.WARN, category, message, data, source);
    }
  }

  /**
   * Error log - enhanced with DystestError support
   */
  error(
    error: Error | DystestError | string,
    data?: Record<string, any>,
    source?: string,
    domain?: string
  ): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;

    let errorDetails: ClinicalErrorDetails;
    let errorMessage: string;
    let errorStack: string | undefined;
    let isDystestError = false;
    let severity: "low" | "medium" | "high" = "medium";
    let errorCode = "UNKNOWN_ERROR";

    if (error instanceof DystestError) {
      errorDetails = error.getDetails();
      errorMessage = error.message;
      errorStack = error.stack;
      isDystestError = true;
      severity = error.severity;
      errorCode = error.code;
    } else if (error instanceof Error) {
      errorMessage = error.message;
      errorStack = error.stack;
      errorDetails = {
        code: "NATIVE_ERROR",
        message: errorMessage,
        timestamp: new Date().toISOString(),
        severity: "medium",
      };
    } else {
      errorMessage = error;
      errorDetails = {
        code: "CUSTOM_ERROR",
        message: errorMessage,
        timestamp: new Date().toISOString(),
        severity: "medium",
      };
    }

    // Map DystestError severity to log category
    const category: LogCategory = domain
      ? (domain as LogCategory)
      : this.mapSeverityToCategory(severity, isDystestError);

    const logData = {
      ...data,
      errorDetails,
      isDystestError,
      errorCode,
      isOperational: isDystestError && (error as DystestError).isOperational,
      isClinical: isClinicalError(error as Error),
      stackTrace: errorStack,
    };

    this.log(LogLevel.ERROR, category, errorMessage, logData, source);
  }

  /**
   * Critical log (for severe clinical errors)
   */
  critical(
    error: Error | DystestError | string,
    data?: Record<string, any>,
    source?: string,
    domain?: string
  ): void {
    if (this.shouldLog(LogLevel.CRITICAL)) {
      const errorObj = typeof error === "string" ? new Error(error) : error;
      this.error(errorObj, { ...data, critical: true }, source, domain);
    }
  }

  /**
   * Log clinical error with domain-specific context
   */
  logClinicalError(
    error: DystestError,
    domain: "VISION" | "GAZE" | "KEYSTROKE" | "CLINICAL",
    context?: Record<string, any>,
    source?: string
  ): void {
    const details = error.getDetails();
    this.log(
      LogLevel.ERROR,
      domain,
      error.message,
      {
        clinicalError: details,
        domain,
        severity: error.severity,
        operational: error.isOperational,
        recoveryAction: error.recoveryAction,
        ...context,
      },
      source
    );
  }

  /**
   * Log vision-specific error
   */
  logVisionError(
    error: DystestError,
    faceData?: { landmarks?: readonly CanonicalLandmark[] } | undefined,
    context?: Record<string, any>,
    source?: string
  ): void {
    const sanitizedFaceData = faceData
      ? {
          ...faceData,
          landmarks: faceData.landmarks
            ? `[REDACTED: ${faceData.landmarks.length} landmarks]`
            : undefined,
        }
      : undefined;

    this.logClinicalError(
      error,
      "VISION",
      {
        ...context,
        faceData: sanitizedFaceData,
      },
      source
    );
  }

  /**
   * Log gaze-specific error
   */
  logGazeError(
    error: DystestError,
    gazePoint?: { x?: number; y?: number } | undefined,
    context?: Record<string, any>,
    source?: string
  ): void {
    const sanitizedGaze = gazePoint
      ? {
          normalized:
            gazePoint.x !== undefined && gazePoint.y !== undefined
              ? `${gazePoint.x.toFixed(3)}, ${gazePoint.y.toFixed(3)}`
              : undefined,
        }
      : undefined;

    this.logClinicalError(
      error,
      "GAZE",
      {
        ...context,
        gazePoint: sanitizedGaze,
      },
      source
    );
  }

  /**
   * Log keystroke-specific error
   */
  logKeystrokeError(
    error: DystestError,
    typingMetrics?: Record<string, any>,
    context?: Record<string, any>,
    source?: string
  ): void {
    this.logClinicalError(
      error,
      "KEYSTROKE",
      {
        ...context,
        typingMetrics: this.sanitizeClinicalData(typingMetrics),
      },
      source
    );
  }

  /**
   * Performance log with clinical context
   */
  logPerformance(
    operation: string,
    duration: number,
    domain?: "VISION" | "GAZE" | "KEYSTROKE" | "CLINICAL" | "UI",
    data?: Record<string, any>,
    source?: string
  ): void {
    if (this.config.enablePerformanceTracking) {
      const category: LogCategory = domain
        ? (`${domain}_PERFORMANCE` as LogCategory)
        : "PERFORMANCE";

      this.log(
        LogLevel.INFO,
        category,
        `${operation} completed`,
        {
          ...data,
          duration,
          domain,
          memoryUsed: this.getMemoryUsage(),
        },
        source
      );

      this.performanceMetrics.set(operation, duration);
    }
  }

  // ============================================================================
  // CORE LOGGING LOGIC
  // ============================================================================

  /**
   * Private: Internal log method
   */
  private log(
    level: LogLevel,
    category: LogCategory,
    message: string,
    data?: Record<string, any>,
    source?: string
  ): void {
    // Create log entry with mutable initial object
    const logEntry: LogEntry = {
      id: this.generateId("log"),
      timestamp: new Date().toISOString(),
      level,
      category,
      message: this.sanitizeString(message),
      data: this.sanitizeData(data),
      source: source || "unknown",
      userId: this.userId,
      sessionId: this.sessionId,
      correlationId: this.generateCorrelationId(),
    };

    // Add stack trace for errors (assign to mutable object before freezing)
    const entryWithStackTrace = {
      ...logEntry,
      ...(level >= LogLevel.ERROR && { stackTrace: this.getStackTrace() }),
    };

    if (this.config.enableBatching) {
      this.addToBuffer(entryWithStackTrace);
    } else {
      this.writeLog(entryWithStackTrace);
    }

    // Notify external services
    this.notifyExternalServices(entryWithStackTrace);
  }

  /**
   * Private: Internal logging for system messages
   */
  private internalLog(
    level: LogLevel,
    category: LogCategory,
    message: string,
    data?: Record<string, unknown> | undefined
  ): void {
    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data: this.sanitizeData(data),
      source: "DystestLogger",
      correlationId: "SYSTEM",
      sessionId: this.sessionId,
    };

    // Always write internal logs immediately
    this.writeLog(logEntry);
  }

  // ============================================================================
  // CLINICAL DATA SANITIZATION
  // ============================================================================

  /**
   * Sanitize clinical data (HIPAA compliance)
   */
  private sanitizeClinicalData(
    data?: Record<string, unknown> | undefined
  ): Record<string, unknown> | undefined {
    if (!data) return data;

    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(data)) {
      // Handle clinical data types
      if (key.includes("landmark") || key.includes("mesh")) {
        if (Array.isArray(value)) {
          sanitized[key] = `[REDACTED: ${value.length} clinical points]`;
        } else {
          sanitized[key] = "[REDACTED_CLINICAL_DATA]";
        }
      } else if (key.includes("gaze") || key.includes("pupil")) {
        if (typeof value === "object" && value !== null) {
          if ("x" in value && "y" in value) {
            sanitized[key] = "[REDACTED_COORDINATES]";
          } else {
            sanitized[key] = "[REDACTED_GAZE_DATA]";
          }
        } else {
          sanitized[key] = value;
        }
      } else if (key.includes("typing") || key.includes("keystroke")) {
        sanitized[key] = "[REDACTED_TYPING_DATA]";
      } else if (key.includes("risk") || key.includes("score")) {
        // Allow risk scores but anonymize context
        if (typeof value === "number") {
          sanitized[key] = value.toFixed(2);
        } else {
          sanitized[key] = "[REDACTED_SCORE]";
        }
      } else {
        // Recursively sanitize nested objects
        if (
          typeof value === "object" &&
          value !== null &&
          !Array.isArray(value)
        ) {
          sanitized[key] = this.sanitizeClinicalData(value as Record<string, unknown>);
        } else {
          sanitized[key] = value;
        }
      }
    }

    return sanitized;
  }

  // ============================================================================
  // UTILITY METHODS FOR CLINICAL INTEGRATION
  // ============================================================================

  /**
   * Map DystestError severity to log category
   */
  private mapSeverityToCategory(
    severity: "low" | "medium" | "high",
    isDystestError: boolean
  ): LogCategory {
    if (!isDystestError) return "TECHNICAL";

    switch (severity) {
      case "high":
        return "SECURITY";
      case "medium":
        return "CLINICAL";
      case "low":
        return "TECHNICAL";
      default:
        return "TECHNICAL";
    }
  }

  // ============================================================================
  // BROWSER CAPABILITIES & MEMORY
  // ============================================================================

  private detectBrowserCapabilities(): void {
    // Check for performance.memory (Chrome/Edge only)
    this.hasMemoryAPI =
      typeof performance !== "undefined" &&
      "memory" in performance &&
      typeof (performance as Performance & { memory?: unknown }).memory === "object";

    // Check for Performance API
    this.hasPerformanceAPI = typeof performance !== "undefined";
  }

  private getMemoryUsage(): number | undefined {
    if (!this.hasMemoryAPI) {
      return undefined;
    }

    try {
      return (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory?.usedJSHeapSize;
    } catch {
      return undefined;
    }
  }

  // ============================================================================
  // SANITIZATION METHODS
  // ============================================================================

  private sanitizeData(
    data?: Record<string, any>,
    depth: number = 0
  ): Record<string, unknown> | undefined {
    if (!data || !this.config.enablePiiProtection) return data;

    // Prevent infinite recursion
    if (depth > 10) {
      return { "[MAX_DEPTH_EXCEEDED]": true };
    }

    // Handle special objects
    if (data instanceof Date) {
      return { date: data.toISOString() };
    }
    if (data instanceof RegExp) {
      return { regex: data.toString() };
    }
    if (data instanceof Error) {
      return {
        name: data.name,
        message: this.sanitizeString(data.message),
        stack: data.stack ? this.sanitizeString(data.stack) : undefined,
      };
    }

    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(data)) {
      // Redact sensitive keys
      if (this.isSensitiveKey(key)) {
        sanitized[key] = "[REDACTED]";
      } else if (Array.isArray(value)) {
        sanitized[key] = value.map((item) =>
          typeof item === "object" && item !== null
            ? this.sanitizeData(item, depth + 1)
            : typeof item === "string"
            ? this.sanitizeString(item)
            : item
        );
      } else if (typeof value === "object" && value !== null) {
        sanitized[key] = this.sanitizeData(value, depth + 1);
      } else if (typeof value === "string") {
        sanitized[key] = this.sanitizeString(value);
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  }

  private sanitizeString(text: string): string {
    if (typeof text !== "string") {
      return text;
    }

    let sanitized = text;

    // Apply default PII patterns
    for (const rule of this.PII_PATTERNS) {
      sanitized = sanitized.replace(rule.pattern, rule.replacement);
    }

    // Apply custom sanitize fields
    for (const field of this.config.sanitizeFields) {
      const fieldPattern = new RegExp(`\\b${field}\\s*[:=]\\s*[^\\s,;]+`, "gi");
      sanitized = sanitized.replace(fieldPattern, `${field}: [REDACTED]`);
    }

    return sanitized;
  }

  private isSensitiveKey(key: string): boolean {
    const sensitiveKeys = [
      "password",
      "token",
      "secret",
      "apiKey",
      "api_key",
      "ssn",
      "creditCard",
      "credit_card",
      "cvv",
      "pin",
      "dob",
      "dateOfBirth",
      "medicalRecord",
      "medical_record",
      "diagnosis",
      "prescription",
      "authorization",
      "auth",
      "bearer",
      "privateKey",
      "private_key",
    ];

    const normalizedKey = key.toLowerCase().replace(/[-_\s]/g, "");
    return sensitiveKeys.some((sensitive) =>
      normalizedKey.includes(sensitive.toLowerCase().replace(/[-_\s]/g, ""))
    );
  }

  // ============================================================================
  // BUFFER MANAGEMENT
  // ============================================================================

  private addToBuffer(entry: LogEntry): void {
    this.logBuffer.push(entry);

    // Flush if buffer is full
    if (this.logBuffer.length >= this.config.maxBatchSize) {
      this.flushBuffer();
    } else if (this.logBuffer.length >= this.config.batchSize) {
      // Schedule flush with browser optimization
      this.scheduleBufferFlush();
    }
  }

  private scheduleBufferFlush(): void {
    if (
      this.config.useIdleCallback &&
      typeof requestIdleCallback !== "undefined"
    ) {
      requestIdleCallback(
        () => {
          this.flushBuffer();
        },
        { timeout: 100 }
      );
    } else {
      // Fallback to immediate execution
      this.flushBuffer();
    }
  }

  // ============================================================================
  // CIRCUIT BREAKER (with mutable state updates)
  // ============================================================================

  private canWriteToDestination(destination: LogDestination): boolean {
    const circuitBreaker = this.circuitBreakers.get(destination);
    if (!circuitBreaker) return true;

    if (circuitBreaker.state === "OPEN") {
      const timeSinceFailure = Date.now() - circuitBreaker.lastFailure;
      const resetTimeout =
        this.config.retryBackoffMs * Math.pow(2, circuitBreaker.failures);

      if (timeSinceFailure > resetTimeout) {
        // Create new mutable object
        const newState: CircuitBreakerState = {
          ...circuitBreaker,
          state: "HALF_OPEN",
          failures: 0,
        };
        this.circuitBreakers.set(destination, newState);
      } else {
        return false;
      }
    }

    return true;
  }

  private recordSuccess(destination: LogDestination): void {
    const circuitBreaker = this.circuitBreakers.get(destination);
    if (circuitBreaker) {
      const newState: CircuitBreakerState = {
        ...circuitBreaker,
        ...(circuitBreaker.state === "HALF_OPEN" && { state: "CLOSED" }),
        failures: 0,
      };
      this.circuitBreakers.set(destination, newState);
    }
  }

  private recordFailure(destination: LogDestination, error: unknown): void {
    const circuitBreaker = this.circuitBreakers.get(destination);
    if (circuitBreaker) {
      const newFailures = circuitBreaker.failures + 1;
      const newState: CircuitBreakerState = {
        failures: newFailures,
        lastFailure: Date.now(),
        state:
          newFailures >= this.config.circuitBreakerThreshold
            ? "OPEN"
            : circuitBreaker.state,
      };
      this.circuitBreakers.set(destination, newState);

      if (newState.state === "OPEN") {
        this.internalLog(LogLevel.WARN, "TECHNICAL", "Circuit breaker opened", {
          destination,
          failures: newState.failures,
        });
      }
    }

    this.internalLog(LogLevel.ERROR, "TECHNICAL", "Write failed", {
      destination,
      error: error instanceof Error ? error.message : "Unknown error",
    });
  }

  // ============================================================================
  // RETRY MECHANISM
  // ============================================================================

  private calculateRetryDelay(attempt: number): number {
    // Exponential backoff: 1s → 2s → 4s → 8s → 16s → 30s (max)
    const exponentialDelay =
      this.config.retryBackoffMs * Math.pow(2, attempt - 1);
    const maxDelay = this.config.retryBackoffMs * 30;
    const cappedDelay = Math.min(exponentialDelay, maxDelay);

    // Add jitter (±20%) to prevent thundering herd
    const jitter = cappedDelay * 0.2 * (Math.random() - 0.5);

    return Math.floor(cappedDelay + jitter);
  }

  private addToRetryQueue(
    destination: LogDestination,
    logs: LogEntry[],
    currentAttempt: number
  ): void {
    const nextAttempt = currentAttempt + 1;
    const delay = this.calculateRetryDelay(nextAttempt);

    const retryQueue = this.retryQueues.get(destination) || [];
    retryQueue.push({
      attempt: nextAttempt,
      nextRetryTime: Date.now() + delay,
      logs: logs.map((log) => ({ ...log, retryCount: nextAttempt })),
    });
    this.retryQueues.set(destination, retryQueue);
  }

  private startRetryTimer(): void {
    if (this.retryTimer) {
      clearInterval(this.retryTimer);
    }

    this.retryTimer = window.setInterval(() => {
      this.processRetryQueues();
    }, 1000);
  }

  private processRetryQueues(): void {
    for (const [destination, retryQueue] of this.retryQueues.entries()) {
      if (retryQueue.length === 0) continue;

      const now = Date.now();
      const readyToRetry: RetryState[] = [];
      const stillWaiting: RetryState[] = [];

      // Separate ready vs waiting
      for (const retry of retryQueue) {
        if (
          retry.nextRetryTime <= now &&
          retry.attempt <= this.config.maxRetries
        ) {
          readyToRetry.push(retry);
        } else {
          stillWaiting.push(retry);
        }
      }

      this.retryQueues.set(destination, stillWaiting);

      // Process ready retries
      for (const retry of readyToRetry) {
        this.processRetry(destination, retry);
      }
    }
  }

  private async processRetry(
    destination: LogDestination,
    retry: RetryState
  ): Promise<void> {
    if (retry.attempt > this.config.maxRetries) {
      console.error(
        `Failed to send ${retry.logs.length} logs to ${destination} after ${this.config.maxRetries} retries`
      );
      return;
    }

    const batchEntry: BatchLogEntry = {
      entries: retry.logs,
      timestamp: new Date().toISOString(),
      batchId: `retry-${this.generateBatchId()}`,
    };

    try {
      await this.writeToDestination(destination, batchEntry);
      this.recordSuccess(destination);
    } catch (error) {
      this.recordFailure(destination, error);
      // Add back to queue for next retry
      this.addToRetryQueue(
        destination,
        retry.logs as LogEntry[],
        retry.attempt
      );
    }
  }

  // ============================================================================
  // BATCH PROCESSING
  // ============================================================================

  private startBatchProcessing(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
    }

    this.batchTimer = window.setInterval(() => {
      this.flushBuffer();
    }, this.config.batchInterval);
  }

  private stopBatchProcessing(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
      this.batchTimer = null;
    }
    if (this.retryTimer) {
      clearInterval(this.retryTimer);
      this.retryTimer = null;
    }
  }

  private async flushBuffer(): Promise<void> {
    if (this.logBuffer.length === 0) return;

    const batch = [...this.logBuffer];
    this.logBuffer = [];

    await this.processBatch(batch);
  }

  private async processBatch(batch: LogEntry[]): Promise<void> {
    const batchEntry: BatchLogEntry = {
      entries: batch,
      timestamp: new Date().toISOString(),
      batchId: this.generateBatchId(),
    };

    // Process each destination
    for (const destination of this.config.destinations) {
      if (this.canWriteToDestination(destination)) {
        try {
          await this.writeToDestination(destination, batchEntry);
          this.recordSuccess(destination);
        } catch (error) {
          this.recordFailure(destination, error);
          await this.handleWriteFailure(destination, batchEntry);
        }
      }
    }
  }

  // ============================================================================
  // DESTINATION WRITERS
  // ============================================================================

  private writeLog(entry: LogEntry): void {
    // Process each destination
    for (const destination of this.config.destinations) {
      if (this.canWriteToDestination(destination)) {
        try {
          this.writeToDestinationSync(destination, entry);
          this.recordSuccess(destination);
        } catch (error) {
          this.recordFailure(destination, error);
          this.handleWriteFailureSync(destination, entry);
        }
      }
    }
  }

  private async writeToDestination(
    destination: LogDestination,
    batch: BatchLogEntry
  ): Promise<void> {
    switch (destination) {
      case "console":
        await this.writeToConsole(batch);
        break;
      case "file":
        await this.writeToFile(batch);
        break;
      case "api":
        await this.writeToApi(batch);
        break;
      case "database":
        await this.writeToDatabase(batch);
        break;
    }
  }

  private writeToDestinationSync(
    destination: LogDestination,
    entry: LogEntry
  ): void {
    switch (destination) {
      case "console":
        this.writeToConsoleSync(entry);
        break;
      case "file":
        this.writeToFileSync(entry);
        break;
      case "api":
        this.writeToApiSync(entry);
        break;
      case "database":
        this.writeToDatabaseSync(entry);
        break;
    }
  }

  private async writeToConsole(batch: BatchLogEntry): Promise<void> {
    console.log(`[BATCH ${batch.batchId}] ${batch.entries.length} entries`);

    for (const entry of batch.entries) {
      this.writeToConsoleSync(entry);
    }
  }

  private writeToConsoleSync(entry: LogEntry): void {
    const color = this.getLogColor(entry.level);
    const timestamp = new Date(entry.timestamp).toLocaleTimeString();
    const levelName = LogLevel[entry.level];

    const logMessage = `[${timestamp}] [${levelName}] [${entry.source}] ${entry.message}`;

    if (entry.data) {
      console.groupCollapsed(`%c${logMessage}`, `color: ${color}`);
      console.dir(entry.data, { depth: 3 });
      console.groupEnd();
    } else {
      console.log(`%c${logMessage}`, `color: ${color}`);
    }

    if (entry.stackTrace && entry.level >= LogLevel.ERROR) {
      console.trace(entry.stackTrace);
    }
  }

  private async writeToFile(batch: BatchLogEntry): Promise<void> {
    // In browser, we simulate file writing
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log(`[FILE] Batch ${batch.batchId} simulated file write`);
        resolve();
      }, 10);
    });
  }

  private writeToFileSync(entry: LogEntry): void {
    console.log(`[FILE] ${JSON.stringify(entry)}`);
  }

  private async writeToApi(batch: BatchLogEntry): Promise<void> {
    if (!this.config.remoteEndpoint) {
      throw new Error("No remote endpoint configured for API destination");
    }

    const payload = {
      logs: batch.entries,
      appName: "DysTest-Healthcare",
      appVersion: this.config.appVersion,
      environment: this.config.environment,
      batchId: batch.batchId,
      sessionId: this.sessionId,
    };

    // Use sendBeacon for better reliability if available
    let sent = false;
    if (
      this.config.useSendBeacon &&
      navigator.sendBeacon &&
      typeof Blob !== "undefined"
    ) {
      const blob = new Blob([JSON.stringify(payload)], {
        type: "application/json",
      });
      sent = navigator.sendBeacon(this.config.remoteEndpoint, blob);
    }

    if (!sent) {
      // Fallback to fetch
      await this.sendWithFetch(payload);
    }
  }

  private async sendWithFetch(payload: unknown): Promise<void> {
    if (!this.config.remoteEndpoint) return;

    const response = await fetch(this.config.remoteEndpoint, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
      keepalive: true,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  }

  private writeToApiSync(entry: LogEntry): void {
    // Would make synchronous HTTP request if possible
    console.log(`[API] ${JSON.stringify(entry)}`);
  }

  private async writeToDatabase(batch: BatchLogEntry): Promise<void> {
    // In browser, simulate database write
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        if (Math.random() > 0.05) {
          // 95% success rate for simulation
          console.log(`[DB] Batch ${batch.batchId} simulated database write`);
          resolve();
        } else {
          reject(new Error("Simulated database write failed"));
        }
      }, 100);
    });
  }

  private writeToDatabaseSync(entry: LogEntry): void {
    console.log(`[DB] ${JSON.stringify(entry)}`);
  }

  // ============================================================================
  // ERROR HANDLING
  // ============================================================================

  private async handleWriteFailure(
    destination: LogDestination,
    batch: BatchLogEntry
  ): Promise<void> {
    // Add batch entries to retry queue
    this.addToRetryQueue(destination, batch.entries as LogEntry[], 0);
  }

  private handleWriteFailureSync(
    destination: LogDestination,
    entry: LogEntry
  ): void {
    // Add to retry queue
    this.addToRetryQueue(destination, [entry], 0);
  }

  // ============================================================================
  // EXTERNAL SERVICE INTEGRATION
  // ============================================================================

  private notifyExternalServices(entry: LogEntry): void {
    for (const service of this.externalServices) {
      try {
        if (entry.level >= LogLevel.ERROR) {
          service.error(entry);
        } else {
          service.log(entry);
        }
      } catch (error) {
        // Silent fail for external service errors
        console.error(`External service ${service.name} error:`, error);
      }
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  private shouldLog(level: LogLevel): boolean {
    return level >= this.config.minLevel;
  }

  private generateId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateCorrelationId(): string {
    return `corr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateBatchId(): string {
    return `batch-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
  }

  private getLogColor(level: LogLevel): string {
    const colors: Record<LogLevel, string> = {
      [LogLevel.DEBUG]: "#808080",
      [LogLevel.INFO]: "#007bff",
      [LogLevel.WARN]: "#ffc107",
      [LogLevel.ERROR]: "#dc3545",
      [LogLevel.AUDIT]: "#28a745",
      [LogLevel.CRITICAL]: "#800000",
    };

    return colors[level] || "#000000";
  }

  private sanitizeConfig(config: LoggerConfig): Partial<LoggerConfig> {
    const sanitized = { ...config };

    // Omit sensitive fields via destructuring rather than delete, which
    // fails on readonly properties and is harder to type-check.
    const { sanitizeFields: _sf, remoteEndpoint: _re, ...stripped } = sanitized;
    return stripped;
  }

  private getStackTrace(): string {
    try {
      const error = new Error();
      const stack = error.stack?.split("\n") || [];
      return stack
        .filter(
          (line) =>
            !line.includes("DystestLogger") &&
            !line.includes("logger.ts") &&
            !line.includes("at new ")
        )
        .slice(0, 10)
        .join("\n");
    } catch {
      return "Stack trace unavailable";
    }
  }

  // ============================================================================
  // PUBLIC UTILITIES
  // ============================================================================

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<LoggerConfig>): void {
    const oldConfig = { ...this.config };
    this.config = { ...this.config, ...newConfig };

    // Handle configuration changes
    if (newConfig.enableBatching !== oldConfig.enableBatching) {
      if (newConfig.enableBatching) {
        this.startBatchProcessing();
      } else {
        this.stopBatchProcessing();
      }
    }

    // Restart batch timer if interval changed
    if (newConfig.batchInterval && this.batchTimer !== null) {
      this.stopBatchProcessing();
      this.startBatchProcessing();
    }

    this.internalLog(LogLevel.AUDIT, "SYSTEM", "Logger configuration updated", {
      oldConfig: this.sanitizeConfig(oldConfig),
      newConfig: this.sanitizeConfig(this.config),
    });
  }

  /**
   * Flush all buffered logs
   */
  async flush(): Promise<void> {
    if (this.logBuffer.length === 0) return;

    const batch = [...this.logBuffer];
    this.logBuffer = [];

    await this.processBatch(batch);
  }

  /**
   * Set user ID for context
   */
  public setUserId(userId: string | undefined): void {
    this.userId = userId;
    this.info("User context updated", {
      userId: userId ? "[SET]" : "[CLEARED]",
    });
  }

  /**
   * Get session ID
   */
  public getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Get logger statistics
   */
  public getStats(): {
    bufferedLogs: number;
    retryQueueSize: number;
    sessionId: string;
    userId?: string | undefined;
    activeTraces: number;
    circuitBreakers: Record<string, CircuitBreakerState>;
  } {
    let totalRetrySize = 0;
    for (const queue of this.retryQueues.values()) {
      totalRetrySize += queue.length;
    }

    const circuitBreakers: Record<string, CircuitBreakerState> = {};
    for (const [dest, state] of this.circuitBreakers.entries()) {
      circuitBreakers[dest] = { ...state };
    }

    return {
      bufferedLogs: this.logBuffer.length,
      retryQueueSize: totalRetrySize,
      sessionId: this.sessionId,
      userId: this.userId,
      activeTraces: this.performanceMarks.size,
      circuitBreakers,
    };
  }

  /**
   * Destroy logger and cleanup
   */
  async destroy(): Promise<void> {
    this.stopBatchProcessing();
    await this.flush();

    // Clear all data
    this.logBuffer = [];
    this.circuitBreakers.clear();
    this.retryQueues.clear();
    this.performanceMetrics.clear();
    this.performanceMarks.clear();
    this.externalServices = [];

    this.internalLog(LogLevel.INFO, "SYSTEM", "Logger destroyed", {});
  }

  // ============================================================================
  // CONVENIENCE EXPORTS
  // ============================================================================

  /**
   * Default logger instance with clinical configuration
   */
  static get clinicalLogger(): DystestLogger {
    return DystestLogger.getInstance({
      minLevel: LogLevel.INFO,
      destinations: isJest || !debugLogsEnabled ? [] : ["console"],
      enableBatching: true,
      batchSize: 10,
      batchInterval: 5000,
      enablePiiProtection: true,
      enablePerformanceTracking: true,
      maxRetries: 3,
      retryBackoffMs: 1000,
      circuitBreakerThreshold: 5,
      sanitizeFields: [],
      useIdleCallback: true,
      useSendBeacon: true,
      environment: "development",
      maxBatchSize: 100,
      appVersion: "1.0.0",
    });
  }

  /**
   * Create a domain-specific logger
   */
  static createVisionLogger(source: string): DystestLogger {
    return DystestLogger.createDomainLogger(source, "VISION");
  }

  static createGazeLogger(source: string): DystestLogger {
    return DystestLogger.createDomainLogger(source, "GAZE");
  }

  static createKeystrokeLogger(source: string): DystestLogger {
    return DystestLogger.createDomainLogger(source, "KEYSTROKE");
  }

  static createClinicalLogger(source: string): DystestLogger {
    return DystestLogger.createDomainLogger(source, "CLINICAL");
  }

  static createUILogger(source: string): DystestLogger {
    return DystestLogger.createDomainLogger(source, "CLINICAL"); // UI logs use CLINICAL category
  }
}

// ============================================================================
// DEFAULT EXPORTS
// ============================================================================

export const logger = DystestLogger.clinicalLogger;
export default DystestLogger;
